// batteryStations.ts — Wave 14cm
// ==============================
//
// Rider-seitiger Client für die Battery-Station-Anzeige. Liest
// /api/battery-stations und cached NICHT — der Server schickt
// Cache-Control: no-store, weil Reservierungs-State immer live sein
// muss (User-Wunsch: "das ist also eine reine online-feature und wird
// bitte NIE gecached"). Auch wenn Phase-1 noch keine Reservierungen
// hat, halten wir das Pattern jetzt schon hart.
//
// Polling-Strategie: fetch beim Mount + bei online-Event + im
// Intervall (Wave 14cn folgt: WebSocket-Live-Update). Für Phase-1
// reicht ein 30-Sekunden-Poll wenn das Sheet offen ist.

import { useEffect, useState } from 'react';
import { authFetch } from './auth';

export interface BatterySlot {
  id: string;
  slotNumber: number;
  batterySizeWh: 800 | 1060;
  currentSoc: number;
  reservedUntil: string | null;
  reservedByUserId: string | null;
  notes: string | null;
  updatedAt: string;
}

export interface BatteryStation {
  id: string;
  name: string;
  lat: number;
  lon: number;
  address: string | null;
  contactNames: string | null;
  openingHours: string | null;
  slotCount: number;
  notes: string | null;
  // Wave 14cn: reservation lead time in Minutes.
  reservationLeadTimeMinutes?: number;
  // Wave 14co: Demo-visible flag. True wenn Admin diese Station auch für
  // Non-VL-User sichtbar gemacht hat. Server gibt nur demo-visible
  // Stationen an Non-VL-User raus, deshalb können wir hier nicht zwischen
  // "real VL" und "demo" unterscheiden — der Backend-Gate ist die Quelle
  // der Wahrheit. Im Frontend nutzen wir die VL-Membership-Info um zu
  // entscheiden ob Reserve-Buttons aktiv oder gated sind.
  demoVisible?: boolean;
  createdAt: string;
  updatedAt: string;
  slots: BatterySlot[];
}

/**
 * Fetch battery stations. Returns null bei 401/403; sonst ein Array
 * (kann leer sein, wenn kein Demo-Eintrag existiert).
 *
 * Wave 14cp-fix: `simulateVl` query param wird durchgereicht — der
 * Admin-Profile-"Simulate Vertical Lodge"-Toggle wirkt damit auch
 * server-seitig (vorher hat der Admin-Bypass alle Stationen geliefert
 * unabhängig vom Toggle, Demo-Modus war nicht testbar).
 */
export async function fetchBatteryStations(opts?: {
  simulateVl?: boolean;
}): Promise<BatteryStation[] | null> {
  try {
    const qs = opts?.simulateVl ? '?simulateVl=1' : '';
    const res = await authFetch(`/api/battery-stations${qs}`);
    if (res.status === 401 || res.status === 403) return null;
    if (!res.ok) return null;
    const body = (await res.json()) as { stations?: BatteryStation[] };
    return Array.isArray(body.stations) ? body.stations : [];
  } catch {
    return null;
  }
}

/**
 * Hook: hält Stations-Liste live. Polled alle 30s wenn enabled.
 * Wave 14cn folgt WebSocket-Push für Reservierungs-Events.
 *
 * Wave 14cp-fix: simulateVl Flag wird an /api/battery-stations
 * weitergereicht — Admin-Profile-Toggle "Simulate VL" wirkt
 * jetzt auch server-seitig (vorher hat der Admin-Bypass alle
 * Stationen geliefert unabhängig vom Simulate-State).
 */
export function useBatteryStations(
  enabled: boolean,
  opts?: { simulateVl?: boolean },
): {
  stations: BatteryStation[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
} {
  const [stations, setStations] = useState<BatteryStation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const simulateVl = !!opts?.simulateVl;

  useEffect(() => {
    if (!enabled) {
      setStations([]);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const data = await fetchBatteryStations({ simulateVl });
        if (cancelled) return;
        if (data === null) {
          setStations([]);
        } else {
          setStations(data);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(String(err instanceof Error ? err.message : err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    // 30s polling — Phase 1 reichend für SoC-Updates per Admin.
    // Phase 2 (Wave 14cn): WebSocket-Push für Reservierungen.
    const interval = window.setInterval(load, 30_000);
    // Reconnect → sofort frisch.
    const onOnline = () => void load();
    window.addEventListener('online', onOnline);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener('online', onOnline);
    };
  }, [enabled, simulateVl]);

  return {
    stations,
    loading,
    error,
    refresh: () => {
      void fetchBatteryStations({ simulateVl }).then((d) => {
        if (d !== null) setStations(d);
      });
    },
  };
}

/** SoC → Farbe nach User-Skala:
 *  rot <30, orange 30-50, gelb 50-80, grün ≥80. */
export function socColor(soc: number): string {
  if (soc < 30) return '#dc2626'; // red-600
  if (soc < 50) return '#f97316'; // orange-500
  if (soc < 80) return '#eab308'; // yellow-500
  return '#22c55e'; // green-500
}

/** True wenn die Reservierungs-Zeit der Slot in der Zukunft liegt
 *  (= aktiv reserviert) und nicht abgelaufen. */
export function isSlotReserved(slot: BatterySlot): boolean {
  if (!slot.reservedUntil) return false;
  return new Date(slot.reservedUntil).getTime() > Date.now();
}

// ─────────────────────────────────────────────────────────────────────────
// Wave 14cp: Reservation flow
// ─────────────────────────────────────────────────────────────────────────

export interface MyReservation {
  slotId: string;
  stationId: string;
  stationName: string;
  slotNumber: number;
  batterySizeWh: 800 | 1060;
  currentSoc: number;
  reservedUntil: string;
  extendedToday: boolean;
}

export async function reserveSlot(
  stationId: string,
  slotId: string,
  opts?: { simulateVl?: boolean },
): Promise<{ ok: true; reservedUntil: string; leadMinutes: number } | { ok: false; error: string }> {
  try {
    // Wave 14cr: ?simulateVl=1 wird durchgereicht — Admin mit Simulate-Toggle
    // soll den Reserve-Flow durchlaufen können, sonst stoppt der VL-Check
    // im Backend mit "vl_required". Server enforced dass nur Admin-Rollen
    // das Flag aktivieren dürfen (kein Backdoor für normale User).
    const qs = opts?.simulateVl ? '?simulateVl=1' : '';
    const res = await authFetch(
      `/api/battery-stations/${encodeURIComponent(stationId)}/slots/${encodeURIComponent(slotId)}/reserve${qs}`,
      { method: 'POST' },
    );
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      return { ok: false, error: body?.error || `http_${res.status}` };
    }
    return {
      ok: true,
      reservedUntil: body.reservedUntil,
      leadMinutes: body.leadMinutes,
    };
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : 'network_error',
    };
  }
}

export async function cancelReservation(
  stationId: string,
  slotId: string,
): Promise<{ ok: boolean; error?: string }> {
  try {
    const res = await authFetch(
      `/api/battery-stations/${encodeURIComponent(stationId)}/slots/${encodeURIComponent(slotId)}/reserve`,
      { method: 'DELETE' },
    );
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      return { ok: false, error: body?.error || `http_${res.status}` };
    }
    return { ok: true };
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : 'network_error',
    };
  }
}

export async function extendReservation(
  stationId: string,
  slotId: string,
): Promise<
  | { ok: true; reservedUntil: string }
  | { ok: false; error: string }
> {
  try {
    const res = await authFetch(
      `/api/battery-stations/${encodeURIComponent(stationId)}/slots/${encodeURIComponent(slotId)}/extend`,
      { method: 'POST' },
    );
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      return { ok: false, error: body?.error || `http_${res.status}` };
    }
    return { ok: true, reservedUntil: body.reservedUntil };
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : 'network_error',
    };
  }
}

export async function fetchMyReservation(): Promise<MyReservation | null> {
  try {
    const res = await authFetch('/api/me/reservation');
    if (!res.ok) return null;
    const body = (await res.json()) as { reservation?: MyReservation | null };
    return body?.reservation || null;
  } catch {
    return null;
  }
}
